import {Component, OnInit} from '@angular/core';
import {NodeBuilder} from "../../../../../../../shared/models";
import {NODE_TYPE, SendRichText} from "./send-rich-text";
import isEmpty from 'lodash/isEmpty';

// Imports
import {
    ClickatellExpansionPanelComponent
} from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle
} from '@angular/material/expansion';
import {InputDirective} from "../../../../../../../shared/directives/form-field/input/input.directive";
import {ActiveFormDirective} from "../../../../../../../shared/directives/active-form/active-form.directive";
import {NextSelectorComponent} from '../../../../json-to-from/next-selector/next-selector.component';

import {SelectMediaComponent} from "../../../../select-media/select-media.component";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {RCS, SMS} from "../../../../../../../shared/utils/config";
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDragPlaceholder,
    CdkDragPreview,
    CdkDropList,
    moveItemInArray
} from "@angular/cdk/drag-drop";
import {FormArray, FormsModule, ReactiveFormsModule, UntypedFormArray} from "@angular/forms";
import {COLLAPSE_EXPAND_STATE, COLLAPSE_TOOLTIP_MESSAGE} from "../../menu/multiple-choice/multipleChoice";
import {NgTemplateOutlet} from "@angular/common";
import {CMSComponent} from "../../../../../../../shared/components/cms/cms.component";
import {MatOption} from "@angular/material/core";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {ChannelNamePipe} from "../../../../../../../shared/utils/pipes/ChannelNamePipe";
import {swalMini} from "../../../../../../../shared/utils/utils";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {isValidVariable, required} from "../../../../../../../shared/utils";
import {
    ClickatellFormValidationComponent
} from "../../../../../../../shared/components/clickatell-form-validation/clickatell-form-validation.component";


@Component({
    selector: 'app-send-rich-text',
    templateUrl: './send-rich-text.component.html',
    styleUrls: ['./send-rich-text.component.scss'],
    imports: [ClickatellExpansionPanelComponent, MatExpansionPanelTitle, InputDirective, NextSelectorComponent, ActiveFormDirective, SelectMediaComponent, MatSlideToggle, FormsModule, ReactiveFormsModule, NgTemplateOutlet, MatAccordion, MatExpansionPanelHeader, MatExpansionPanel, CMSComponent, CdkDropList, CdkDrag, CdkDragPlaceholder, CdkDragPreview, CdkDragHandle, MatOption, MatSelect, ChannelNamePipe, MatSelectTrigger, MatButtonToggle, MatButtonToggleGroup, ClickatellFormValidationComponent]
})
export class SendRichTextComponent extends NodeBuilder implements OnInit {

    protected readonly CHANNELS_SUPPORTED = [SMS, RCS]

    collapseExpandStates: COLLAPSE_EXPAND_STATE = COLLAPSE_EXPAND_STATE.OPEN;
    collapseExpandTooltip: COLLAPSE_TOOLTIP_MESSAGE = COLLAPSE_TOOLTIP_MESSAGE.EXPANDED;
    selectedMediaType: 'assets' | 'variable' = 'assets';
    showButtonErrorMessage: boolean = false;

    protected readonly SMS_WARNING_MESSAGE: string = "SMS will be split into 2 or more messages if over limit. RCS will send as a single message."
    protected readonly BUTTON_TOOLTIP_MESSAGE: string = "Buttons are limited to 4 per message. Adding a 5th will convert this to a numbered list menu and postback data won't be available for any options.";
    protected readonly BUTTON_REQUIRED_MESSAGE: string = "One button is required when using media.";

    protected readonly SMS = SMS;
    protected readonly RCS = RCS;

    protected readonly CHANNEL_LIMITS = {
        [SMS]: 160,
        [RCS]: 2000,
    }

    sendRichText: SendRichText;

    constructor() {
        super(NODE_TYPE);
    }

    ngOnInit(): void {
        this.sendRichText = new SendRichText();
    }

    set buildForm(stepData: any) {
        this.formGroup = this.sendRichText.toForm(stepData, this.canEdit)
        this.setAllNextStepValues = ['success', 'error'];

        if (!isEmpty(stepData)) {
            this.setMediaType();
        }
    }

    get getStepData(): {} {
        this.sendRichText.setNodeData(this.formGroup.getRawValue());
        return this.sendRichText.nodeData;
    }

    get isNodeValid(): boolean {
        this.showButtonErrorMessage = this.mediaValidation();
        if (this.showButtonErrorMessage) {
            const options = this.formGroup.get('items') as FormArray;
            if (options.length === 0) {
                return false;
            }
        }

        return this.isValidateForm();
    }

    public setImage(event): void {
        this.formGroup.get('imageRef').setValue(event);
        this.showButtonErrorMessage = this.mediaValidation();
    }

    public mediaValidation(): boolean {
        const imageRef: string = this.formGroup.get('imageRef').value;
        const channelId = this.formGroup.get('channelID').value;
        if (channelId === this.SMS) {
            return false;
        }

        if ((imageRef === undefined || imageRef.length === 0)) {
            return false;
        }

        return true;
    }

    private setMediaType(): void {
        if (this.formGroup) {
            const control = this.formGroup.get('imageRef');
            if (control) {
                const val: string = control.value;
                if (val.startsWith('[[') && val.endsWith(']]')) {
                    this.selectedMediaType = 'variable';
                    this.formGroup.get('imageRef').setValidators([isValidVariable()]);
                    return;
                }
            }
        }
        this.selectedMediaType = 'assets';
        this.showButtonErrorMessage = this.mediaValidation();
    }

    public mediaTypeChange(event: MatButtonToggleGroup): void {
        if (this.formGroup.get('imageRef')) {
            this.formGroup.get('imageRef').patchValue('');
            if (event.value === 'variable') {
                this.formGroup.get('imageRef').setValidators([isValidVariable()]);
            } else {
                this.formGroup.get('imageRef').setValidators([]);
            }
        }
        this.selectedMediaType = event.value;
    }

    public collapseExpandLogic(accordion: MatAccordion): void {

        if (this.collapseExpandStates === COLLAPSE_EXPAND_STATE.OPEN) {

            this.collapseExpandTooltip = COLLAPSE_TOOLTIP_MESSAGE.EXPANDED;
            this.collapseExpandStates = COLLAPSE_EXPAND_STATE.CLOSE;

            accordion.closeAll();
            return;
        }

        accordion.openAll();
        this.collapseExpandTooltip = COLLAPSE_TOOLTIP_MESSAGE.COLLAPSED;
        this.collapseExpandStates = COLLAPSE_EXPAND_STATE.OPEN;
        return;
    }

    public drop(event: CdkDragDrop<UntypedFormArray[]>, accordion: MatAccordion) {
        if (event.previousContainer === event.container) {
            const options = this.formGroup.get('items') as FormArray;
            const currentList = options.getRawValue();
            moveItemInArray(currentList, event.previousIndex, event.currentIndex);
            options.patchValue(currentList);
        }
    }

    public deleteOption(index: number, event: any) {
        event.stopPropagation();
        if (!this.canEdit.canEdit) {
            return;
        }

        const imageRef: string = this.formGroup.get('imageRef').value;
        const options = this.formGroup.get('items') as FormArray;
        const channelId = this.formGroup.get('channelID').value;

        switch (channelId) {
            case this.RCS: {

                if ((imageRef === undefined || imageRef.length === 0)) {
                    options.removeAt(index);
                    return;
                }

                if (options.length === 1) {
                    swalMini({title: 'At least one option needed', type: 'error'});
                    return;
                }
                options.removeAt(index);
            }
            default: {
                options.removeAt(index);
                return;
            }
        }
    }

    public addOption() {
        const options = this.formGroup.get('items') as FormArray;
        const itemFormGroup = this.sendRichText.itemTemplate({
            text: "",
            additionalData: "",
            next: ""
        }, this.canEdit.canEditFlows);
        options.push(itemFormGroup)
    }
}
